import { type FC, useCallback } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { IconCheckFill } from "@/assets/icons/geist/IconCheckFill";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { cn } from "@/components/podkit/lib/cn";
import { AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import type { TodoItem } from "@/components/ai/chat/TodoList";

export const TodoListItem: FC<{ todo: TodoItem; index: number }> = ({ todo, index }) => {
    const isCompleted = todo.status === AgentResponseBlock_TodoItem_Phase.DONE;
    const isInProgress = todo.status === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS;
    const isCleared = todo.status === AgentResponseBlock_TodoItem_Phase.CLEARED;

    const getStatusLabel = () => {
        if (isCompleted) return "Completed";
        if (isInProgress) return "In progress";
        if (isCleared) return "Cleared";
        return "Pending";
    };

    const handleClick = useCallback(() => {
        if (
            todo.status !== AgentResponseBlock_TodoItem_Phase.IN_PROGRESS &&
            todo.status !== AgentResponseBlock_TodoItem_Phase.DONE
        ) {
            return;
        }

        const targetElement = document.getElementById(`todo-${todo.todoItemId}`);
        if (targetElement) {
            targetElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [todo.todoItemId, todo.status]);

    const ItemContent = (
        <>
            <Text
                className={cn(
                    "flex-1 font-mono text-base",
                    !isCompleted && !isInProgress && !isCleared ? "text-content-tertiary" : "",
                    isCleared && "text-content-tertiary line-through",
                )}
            >
                {index + 1}. {todo.title}
            </Text>
            <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                {isCompleted ? (
                    <IconCheckFill size="sm" className="text-content-green" aria-hidden="true" />
                ) : isInProgress ? (
                    <IconSpinner size="sm" className="animate-spin text-brand-foreground" aria-hidden="true" />
                ) : null}
            </div>
        </>
    );

    if (isInProgress || isCompleted) {
        return (
            <button
                key={todo.todoItemId}
                data-todo-item
                className="flex w-full items-center gap-1 rounded-sm px-1.5 py-1 text-left transition-colors hover:bg-surface-muted"
                aria-label={`${getStatusLabel()}: ${todo.title} - Click to scroll to section`}
                onClick={handleClick}
                data-tracking-id="todo-item"
            >
                {ItemContent}
            </button>
        );
    }

    return (
        <div
            key={todo.todoItemId}
            data-todo-item
            role="listitem"
            className={cn(
                "flex w-full items-center gap-1 rounded-sm px-1.5 py-1 text-left text-sm transition-colors",
                "opacity-60",
            )}
            aria-label={`${getStatusLabel()}: ${todo.title}`}
        >
            {ItemContent}
        </div>
    );
};
